import { createRoute } from '@tanstack/react-router';
import { authLayoutRoute, publicLayoutRoute } from '@/app/routes/roots';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage';
import { ActivateAgencyPage } from './pages/ActivateAgencyPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SignUpPage } from './pages/SignUpPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';

/**
 * Signed-out routes.
 *
 * Every module owns its routes in a file like this and exports them from
 * index.ts; `app/router.tsx` only assembles the list. Adding a module means
 * touching that one assembly line, never editing a central route table.
 *
 * Auth pages are NOT lazy-loaded: sign-in is the first screen an unauthenticated
 * visitor sees, so putting it behind an extra chunk request makes the app feel
 * slower exactly where first impressions are formed.
 *
 * Paths and search-param names here are dictated by the links the backend puts
 * in emails (`apps/platform/src/auth/auth.factory.ts` and
 * `apps/admin/src/agencies/agencies.service.ts`). They are not free to rename
 * on this side alone — a mismatch is a 404 for a real user holding a valid
 * token, and links already sent cannot be recalled.
 */
export const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === 'string' && search.next.startsWith('/') ? search.next : undefined,
  }),
});

/** Public self-signup: creates an agency and its owner. */
export const signUpRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/signup',
  component: SignUpPage,
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

export const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

/** Emailed as `{INVITE_LINK_BASE_URL}/activate-agency?token=<uuid>`. */
export const activateAgencyRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/activate-agency',
  component: ActivateAgencyPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

/** Emailed as `{INVITE_LINK_BASE_URL}/accept-invitation?invitationId=<uuid>`. */
export const acceptInvitationRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/accept-invitation',
  component: AcceptInvitationPage,
  validateSearch: (search: Record<string, unknown>): { invitationId?: string } => ({
    invitationId: typeof search.invitationId === 'string' ? search.invitationId : undefined,
  }),
});

/**
 * Emailed as `{INVITE_LINK_BASE_URL}/verify-email?token=<jwt>` after self-signup.
 * Public rather than `_auth`: the link may be opened on a machine where someone
 * else is signed in, and it replaces that session with the verified user's.
 */
export const verifyEmailRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/verify-email',
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

/** Guarded by `_auth`: signed-in visitors are redirected away. */
export const authRoutes = [loginRoute, signUpRoute, forgotPasswordRoute, resetPasswordRoute];

/** Unguarded: reachable whether or not someone is already signed in. */
export const publicAuthRoutes = [activateAgencyRoute, acceptInvitationRoute, verifyEmailRoute];
